import type { SQLiteDatabase } from 'expo-sqlite';

import { newId, now } from '@/db/ids';
import type { ContactRow } from '@/db/types';
import { bumpDataRevision } from '@/lib/data-revision';

export async function listContacts(
  db: SQLiteDatabase,
  propertyId: string
): Promise<ContactRow[]> {
  return await db.getAllAsync<ContactRow>(
    `SELECT * FROM contact
     WHERE property_id = ? AND deleted_at IS NULL
     ORDER BY last_used_at DESC, name ASC`,
    propertyId
  );
}

export async function getContactById(
  db: SQLiteDatabase,
  id: string
): Promise<ContactRow | null> {
  return await db.getFirstAsync<ContactRow>(
    `SELECT * FROM contact WHERE id = ? AND deleted_at IS NULL`,
    id
  );
}

export interface NewContactInput {
  propertyId: string;
  name: string;
  role?: string | null;
  company?: string | null;
  phone?: string | null;
  email?: string | null;
  website?: string | null;
  notes?: string | null;
  /** Document id of the scanned business-card image (v48). */
  cardDocId?: string | null;
}

export async function addContact(
  db: SQLiteDatabase,
  input: NewContactInput
): Promise<string> {
  const id = newId();
  const t = now();
  await db.runAsync(
    `INSERT INTO contact (
      id, property_id, name, role, company, phone, email, website, notes,
      card_doc_id, created_at, updated_at
    ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
    id,
    input.propertyId,
    input.name.trim(),
    input.role ?? null,
    input.company ?? null,
    input.phone ?? null,
    input.email ?? null,
    input.website ?? null,
    input.notes ?? null,
    input.cardDocId ?? null,
    t,
    t
  );
  bumpDataRevision();
  return id;
}

export interface UpdateContactInput {
  name?: string;
  role?: string | null;
  company?: string | null;
  phone?: string | null;
  email?: string | null;
  website?: string | null;
  notes?: string | null;
  card_doc_id?: string | null;
}

export async function updateContact(
  db: SQLiteDatabase,
  id: string,
  input: UpdateContactInput
): Promise<void> {
  const t = now();
  const sets: string[] = [];
  const args: (string | number | null)[] = [];
  const fields: (keyof UpdateContactInput)[] = [
    'name', 'role', 'company', 'phone', 'email', 'website', 'notes',
    'card_doc_id',
  ];
  for (const f of fields) {
    if (input[f] !== undefined) {
      sets.push(`${f} = ?`);
      args.push((input[f] as string | null) ?? null);
    }
  }
  if (sets.length === 0) return;
  sets.push('updated_at = ?');
  args.push(t);
  args.push(id);
  await db.runAsync(`UPDATE contact SET ${sets.join(', ')} WHERE id = ?`, ...args);
  bumpDataRevision();
}

export async function markContactUsed(
  db: SQLiteDatabase,
  id: string
): Promise<void> {
  const t = now();
  await db.runAsync(
    `UPDATE contact SET last_used_at = ?, updated_at = ? WHERE id = ?`,
    t,
    t,
    id
  );
}

export async function deleteContact(db: SQLiteDatabase, id: string): Promise<void> {
  const t = now();
  await db.runAsync(
    `UPDATE contact SET deleted_at = ?, updated_at = ? WHERE id = ?`,
    t,
    t,
    id
  );
  bumpDataRevision();
}

export const COMMON_ROLES = [
  'Plumber',
  'Electrician',
  'HVAC technician',
  'Roofer',
  'Landscaper',
  'Arborist',
  'Pest control',
  'Septic service',
  'Chimney sweep',
  'Painter',
  'Handyman',
  'Cleaner',
  'Pool service',
  'Snow removal',
  'Generator service',
  'Home inspector',
  'Insurance agent',
  'Realtor',
] as const;
